import { useState, useMemo, useEffect } from 'react';
import { filterStrings } from '@utils/arrays/filter-strings';

/** Helper types */
type SelectedLanguage = ApiSchema<'SpokenLanguageWrite2'>;
type OptionItem = ApiSchema<'OptionItem'>;

/** Props */
interface UseJobLanguagesProps {
  initialSelectedValues?: SelectedLanguage[],
  apiSpokenLanguages?: OptionItem[],
}

/**
 * @description Hook to manage the languages in the job form.
 *
 * ** Props **
 * - `initialSelectedValues` - the initial selected languages from API.
 * - `apiSpokenLanguages` - the list of spoken languages from API.
 *
 * ** Returns **
 * - `staticList` - the static list for the combobox; the already selected languages are removed.
 * - `selectedLanguages` - the selected languages for the form.
 * - `filterStaticList` - the function to filter the static list for the combobox.
 * - `addLanguage` - the function to add a language to the selected languages.
 * - `deleteLanguage` - the function to delete a language from the selected languages.
 * - `changeLanguageLevel` - the function to change the language level.
 *
 * ** Note **
 * The language levels can be changed inside the `JobLanguagePill` component using the `changeLanguageLevel` function.
 */
export const useJobLanguages = (props: UseJobLanguagesProps) => {
  // Destructure props
  const {
    initialSelectedValues = [],
    apiSpokenLanguages = [],
  } = props;

  // The selected languages
  // ********************
  const [selectedLanguages, setSelectedLanguages] = useState<SelectedLanguage[]>(initialSelectedValues);

  // Reset selected languages if initialSelectedValues change
  useEffect(() => {
    setSelectedLanguages(initialSelectedValues);
  }, [initialSelectedValues]);

  // The static list for the combobox.
  // Remove the already selected languages from the Combobox dropdown.
  const staticList = useMemo(
    () => apiSpokenLanguages
      .filter((language) => !selectedLanguages
        .find((selectedLanguage) => selectedLanguage.shortName === language.id)),
    [apiSpokenLanguages, selectedLanguages],
  );

  // Filter static list for filteringFunction Combobox prop.
  // Function to narrow down the list of languages as the user types.
  const filterStaticList = (
    inputValue: string | undefined,
  ) => filterStrings(inputValue, 'includes', ['name']);

  // Add a language to the selected languages.
  // Use it inside onSelectedItemChange Combobox prop.
  const addLanguage = (languageObj?: OptionItem) => {
    if (languageObj && languageObj?.id && languageObj?.name) {
      setSelectedLanguages((prev) => {
        const languages = [...prev];
        const newLanguage = { shortName: languageObj.id, level: 0 } as unknown as SelectedLanguage;
        if (!languages.find((selectedLanguage) => selectedLanguage.shortName === languageObj.id)) {
          languages.push(newLanguage);
        }
        return languages;
      });
    }
  };

  // Delete a language from the selected languages.
  // Use it inside deleteLanguage prop in LanguagePill.
  const deleteLanguage = (languageShortName?: string) => {
    if (languageShortName) {
      setSelectedLanguages((prev) => prev
        .filter((language) => language.shortName !== languageShortName));
    }
  };

  // Change the language level.
  // Use it inside changeLanguageLevel prop in LanguagePill.
  const changeLanguageLevel = (languageShortName?: string, languageLevel?: string) => {
    if (languageShortName && languageLevel) {
      setSelectedLanguages((prev) => prev
        .map((language) => {
          if (language.shortName === languageShortName) {
            return { ...language, level: parseInt(languageLevel, 10) } as unknown as SelectedLanguage;
          }
          return language;
        }));
    }
  };

  // Return
  // ********************
  return {
    staticList,
    selectedLanguages,
    filterStaticList,
    addLanguage,
    deleteLanguage,
    changeLanguageLevel,
  };
};
